import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";
import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector, useDispatch } from "react-redux";
import {
  deleteUserById,
  fetchAllUsers,
  toggleUserStatus,
  deleteUserAlls,
} from "../../redux/user/userThunks";
import { setFilters } from "../../redux/user/userSlice";
import { getModulePermissions } from "../../utils/permissionUtils";
import { fetchAvailableCities } from "../services/analyticsService";
import { grantTrialToUsers, getUserLifecycle, hardDeleteUser, hardDeleteUsersBulk } from "../services/userService";
import { openGrantTrialModal } from "./openGrantTrialModal";
import { isPaused, pauseLine, lifecycleIntroLine } from "./introPauseUtils";
import { isNameFlagged, flaggedLine, NAME_REVIEW_LABEL } from "./nameReviewUtils";

const UserListLayer = () => {
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  // Account view: signup date + why they deleted (founder 2026-09-02).
  const [lifecycle, setLifecycle] = useState(null); // { loading, data, error }

  const openLifecycle = async (user) => {
    setLifecycle({ loading: true, data: null, header: user });
    try {
      const res = await getUserLifecycle(user._id);
      setLifecycle({ loading: false, data: res?.data?.data || null, header: user });
    } catch (err) {
      setLifecycle({ loading: false, data: null, header: user, error: true });
    }
  };
  const closeLifecycle = () => setLifecycle(null);
  const navigate = useNavigate();
  const location = useLocation();

  const headerCheckboxRef = useRef(null);
  const [selectAllGlobal, setSelectAllGlobal] = useState(false);
  const [filtersInitialized, setFiltersInitialized] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  


  const authToken = localStorage.getItem("authToken");
  const { shouldCheckPermissions, canEdit, canAdd, canView, canDelete } =
    getModulePermissions("User");

  // Inside the component:
  const dispatch = useDispatch();
  const {
    userList: items,
    listPausedFilter,
    totalPages,
    totalUsers,
    loading,
    error,
    filters: {
      currentPage,
      itemsPerPage,
      searchQuery,
      city,
      sortColumn,
      sortOrder,
      statusFilter,
      missingFilter,
      pausedFilter,
      nameReviewFilter,
      startDate,
      endDate,
    },
  } = useSelector((state) => state.user);

  // Replace your fetchData with:
  const fetchData = (page) => {
    const formattedStartDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : "";
    const formattedEndDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : "";

    const params = {
      page: page,
      limit: itemsPerPage,
      search: searchQuery,
      city: city,
      sortBy: sortColumn,
      sortOrder: sortOrder,
      // Soft-deleted accounts stay out of every live view; the "Deleted"
      // status option lists exactly them (server param: deleted=hide|only).
      status: statusFilter === "deleted" ? "" : statusFilter,
      deleted: statusFilter === "deleted" ? "only" : "hide",
      // "Missing" filter (2026-09-07): members with an empty field the
      // table would print as N/A (server param: missing=name|age|...).
      missing: missingFilter || "",
      // Introductions paused (2026-09-27, server param: paused=only).
      paused: pausedFilter === "only" ? "only" : "",
      // Name needs a look (2.6, 2026-10-15, server param: nameReview=only).
      nameReview: nameReviewFilter === "only" ? "only" : "",
      startDate: formattedStartDate,
      endDate: formattedEndDate,
    };

    dispatch(fetchAllUsers({ params, navigate }));
  };

  useEffect(() => {
    fetchData(currentPage);
  }, [
    dispatch,
    navigate,
    currentPage,
    searchQuery,
    city,
    itemsPerPage,
    sortOrder,
    sortColumn,
    statusFilter,
    missingFilter,
    pausedFilter,
    nameReviewFilter,
    startDate,
    endDate,
  ]);

  const isSelected = (id) => selectedItems.has(id);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const res = await fetchAvailableCities();
        if (res.data?.status) {
          setCityOptions(res.data.data || []);
        }
      } catch (err) {
        console.error("Failed to load cities", err);
      }
    };

    loadCities();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    const statusParam = params.get("status") || "";
    const startParam = params.get("startDate")
      ? new Date(params.get("startDate"))
      : null;
    const endParam = params.get("endDate")
      ? new Date(params.get("endDate"))
      : null;

    // Only set filters from URL if not already initialized
    if (!filtersInitialized) {
      dispatch(
        setFilters({
          statusFilter: statusParam,
          startDate: startParam
            ? moment(startParam).format("YYYY-MM-DD")
            : null,
          endDate: endParam ? moment(endParam).format("YYYY-MM-DD") : null,
          currentPage: 1,
        })
      );
      setFiltersInitialized(true);
    }
  }, [location.search, dispatch, filtersInitialized]);
  const handleDeleteAll = async (id, single = "No") => {
    let idsToDelete = [];

    if (single === "Yes") {
      idsToDelete = [id];
    } else if (selectAllGlobal) {
      // If global select is on, tell backend to delete all matching users
      idsToDelete = "ALL"; // special flag, backend interprets this
    } else {
      idsToDelete = Array.from(selectedItems);
    }

    // Show confirmation message
    const message =
      idsToDelete === "ALL"
        ? `This action will delete all ${totalUsers} users!`
        : idsToDelete.length === 1
          ? "This action will delete the user!"
          : `This action will delete ${idsToDelete.length} users!`;

    const confirmMessage =
      idsToDelete === "ALL"
        ? "Delete all users"
        : idsToDelete.length === 1
          ? "Delete user"
          : "Delete users";

    const result = await showConfirmationDialog(
      message,
      confirmMessage,
      "Cancel"
    );

    if (!result.isConfirmed) return;

    try {
      await dispatch(deleteUserAlls({ id: idsToDelete, navigate })).unwrap();

      setSuccessMessage(
        idsToDelete === "ALL"
          ? "All users deleted successfully"
          : "Users deleted successfully"
      );
      setSelectedItems(new Set());
      setSelectAllGlobal(false);
      fetchData(currentPage);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      dispatch(setFilters({ currentPage: pageNumber }));
    }
  };

  const handleSort = (column) => {
    const newOrder =
      sortColumn === column && sortOrder === "asc" ? "desc" : "asc";

    dispatch(setFilters({ sortColumn: column, sortOrder: newOrder }));
  };

  const handleItemsPerPageChange = (e) => {
    dispatch(
      setFilters({
        itemsPerPage: parseInt(e.target.value, 10),
        currentPage: 1, // reset to first page
      })
    );
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      dispatch(setFilters({ currentPage: currentPage + 1 }));
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      dispatch(setFilters({ currentPage: currentPage - 1 }));
    }
  };

  const updateStatus = async (id, currentStatus, nextStatus) => {
    if (currentStatus === nextStatus) return;

    const result = await showConfirmationDialog(
      `Change status from ${currentStatus} → ${nextStatus}?`,
      "Confirm Status Change",
      "Cancel"
    );

    if (!result.isConfirmed) return;

    try {
      await dispatch(
        toggleUserStatus({
          id,
          data: { status: nextStatus },
          navigate,
        })
      ).unwrap();

      setSuccessMessage(`User status changed to ${nextStatus}`);
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err?.message || "Failed to update status");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };


  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4); // Ensure only 5 items are shown (start + 4)

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  const handleHardDeleteSelected = async () => {
    const ids = Array.from(selectedItems);
    if (ids.length === 0) return;
    const result = await showConfirmationDialog(
      `PERMANENTLY delete ${ids.length} selected account${ids.length === 1 ? "" : "s"} and ALL of their data? This CANNOT be undone. (The founder and admin accounts are skipped automatically.)`,
      "Permanently delete selected",
      "Cancel"
    );
    if (!result.isConfirmed) return;
    try {
      const res = await hardDeleteUsersBulk(ids);
      setSuccessMessage(res?.data?.message || "Selected users permanently deleted");
      setSelectedItems(new Set());
      setSelectAllGlobal(false);
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Failed to permanently delete selected users");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3500);
    }
  };

  const handleHardDelete = async (user) => {
    const name = user?.fullName || user?.email || "this account";
    const result = await showConfirmationDialog(
      `PERMANENTLY delete ${name}? This erases the account and all of its data (introductions, chats, activity, photos) and CANNOT be undone.`,
      "Permanently delete",
      "Cancel"
    );
    if (!result.isConfirmed) return;
    try {
      const res = await hardDeleteUser(user._id);
      setSuccessMessage(res?.data?.message || "User permanently deleted");
      setSelectedItems((prev) => {
        const updated = new Set(prev);
        updated.delete(user._id);
        return updated;
      });
      setSelectAllGlobal(false);
      setTimeout(() => {
        if (items.length === 1 && currentPage > 1) {
          dispatch(setFilters({ currentPage: currentPage - 1 }));
        } else {
          fetchData(currentPage);
        }
      }, 200);
    } catch (err) {
      setErrorMessage(err?.response?.data?.message || "Failed to permanently delete user");
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3500);
    }
  };

  const handleDeleteUser = async (id) => {
    const result = await showConfirmationDialog(
      "This action will delete User!",
      "Delete User",
      "Cancel"
    );
    if (!result.isConfirmed) return;

    try {
      const data = await dispatch(deleteUserById({ id, navigate })).unwrap();
      setSuccessMessage(data?.message || "User deleted successfully");
      // ✅ Remove the deleted user from selectedItems
      setSelectedItems((prev) => {
        const updated = new Set(prev);
        updated.delete(id);
        return updated;
      });

      // ✅ Reset global select if needed
      setSelectAllGlobal(false);

      setTimeout(() => {
        if (items.length === 1 && currentPage > 1) {
          dispatch(setFilters({ currentPage: currentPage - 1 }));
        } else {
          fetchData(currentPage);
        }
      }, 200);
    } catch (err) {
      const errorMsg =
        typeof err === "string" ? err : err?.message || "Failed to delete user";
      setErrorMessage(errorMsg);
    } finally {
      setTimeout(() => {
        setSuccessMessage("");
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleSelectItem = (id) => {
    setSelectAllGlobal(false); // cancel global mode when manual changes happen
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) updated.delete(id);
      else updated.add(id);
      return updated;
    });
  };

  // Select/deselect all on current page
  const handleSelectAll = (checked) => {
    setSelectAllGlobal(false); // reset global mode
    setSelectedItems((prev) => {
      const updated = new Set(prev);
      items.forEach((user) => {
        if (checked) updated.add(user._id);
        else updated.delete(user._id);
      });
      return updated;
    });
  };

  // Header checkbox helpers
  const currentPageIds = items.map((u) => u._id);
  const isHeaderChecked = () => {
    if (selectAllGlobal) return true; // all pages
    return currentPageIds.every((id) => selectedItems.has(id));
  };

  const isHeaderIndeterminate = () => {
    if (selectAllGlobal) return false; // no indeterminate if global
    return (
      currentPageIds.some((id) => selectedItems.has(id)) && !isHeaderChecked()
    );
  };

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        !selectAllGlobal && isHeaderIndeterminate();
    }
  }, [items, selectedItems, selectAllGlobal]);





  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between cz-toolbar">
        <div className="cz-topline">
          <form className="navbar-search cz-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => {
                dispatch(
                  setFilters({
                    searchQuery: e.target.value,
                  })
                );
              }}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  dispatch(setFilters({ currentPage: 1 }));
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />

            {/* Search Icon */}
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />

            {/* Clear (Close) Icon */}
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      searchQuery: "",
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <div className="cz-actions">
            {(!shouldCheckPermissions || canDelete) && (
              <button
                className="btn btn-outline-danger"
                onClick={handleDeleteAll}
                disabled={selectedItems.size === 0}
                title="Soft delete: marks the accounts deleted but keeps the records"
              >
                Delete selected
              </button>
            )}
            {(!shouldCheckPermissions || canDelete) && (
              <button
                className="btn btn-danger"
                onClick={handleHardDeleteSelected}
                disabled={selectedItems.size === 0}
                title="Permanently erase the selected accounts and all their data: cannot be undone"
              >
                Delete permanently
              </button>
            )}
            {(!shouldCheckPermissions || canEdit) && (
              <button
                className="btn btn-primary border-0"
                disabled={selectedItems.size === 0 && !selectAllGlobal}
                onClick={() =>
                  openGrantTrialModal(
                    Array.from(selectedItems),
                    selectAllGlobal,
                    dispatch,
                    navigate,
                    setSelectedItems,
                    () => fetchData(currentPage)
                  )
                }
              >
                Grant Trial
              </button>
            )}
          </div>
        </div>

        <div className="cz-filters">
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) =>
              dispatch(
                setFilters({
                  statusFilter: e.target.value,
                  currentPage: 1, // reset to page 1 on filter change
                })
              )
            }
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
            <option value="deleted">Deleted</option>
          </select>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={city}
            onChange={(e) =>
              dispatch(
                setFilters({
                  city: e.target.value,
                  currentPage: 1,
                })
              )
            }
          >
            <option value="">All Cities</option>
            {cityOptions.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {/* Missing-field filter (founder 2026-09-07): "N/A" in the table
              is a placeholder, not data, so searching it found nothing. */}
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={missingFilter || ""}
            title="Members with this field empty (shown as N/A)"
            onChange={(e) =>
              dispatch(
                setFilters({
                  missingFilter: e.target.value,
                  currentPage: 1,
                })
              )
            }
          >
            <option value="">Nothing missing</option>
            <option value="location">Missing location</option>
            <option value="height">Missing height</option>
            <option value="name">Missing name</option>
            <option value="age">Missing age</option>
            <option value="photos">Missing photos</option>
          </select>
          {/* Introductions paused (founder go 2026-09-27): who asked to
              stop being introduced, and is out of every deal until resumed. */}
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={pausedFilter || ""}
            title="Members whose introductions are paused"
            onChange={(e) =>
              dispatch(
                setFilters({
                  pausedFilter: e.target.value,
                  currentPage: 1,
                })
              )
            }
          >
            <option value="">Introductions: all</option>
            <option value="only">Introductions paused</option>
          </select>
          {/* Name needs a look (2.6, founder 2026-10-15): new sign-ups who
              kept a name the name check doubted. Existing members are never
              listed here. */}
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={nameReviewFilter || ""}
            title="New sign-ups whose name waits for a look"
            aria-label="Names"
            onChange={(e) =>
              dispatch(
                setFilters({
                  nameReviewFilter: e.target.value,
                  currentPage: 1,
                })
              )
            }
          >
            <option value="">Names: all</option>
            <option value="only">{NAME_REVIEW_LABEL}</option>
          </select>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                dispatch(
                  setFilters({
                    startDate: date,
                    currentPage: 1,
                  })
                );
              }}
              className="form-control"
              placeholderText="Start Date"
              dateFormat="yyyy-MM-dd"
            />

            {startDate && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      startDate: null,
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>

          <div style={{ position: "relative", maxWidth: "200px" }}>
            <DatePicker
              selected={endDate}
              onChange={(date) => {
                dispatch(
                  setFilters({
                    endDate: date,
                    currentPage: 1,
                  })
                );
              }}
              className="form-control"
              placeholderText="End Date"
              dateFormat="yyyy-MM-dd"
            />

            {endDate && (
              <button
                type="button"
                onClick={() => {
                  dispatch(
                    setFilters({
                      endDate: null,
                      currentPage: 1,
                    })
                  );
                }}
                style={{
                  position: "absolute",
                  right: "10px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "transparent",
                  border: "none",
                  fontSize: "1.25rem",
                  color: "#000",
                  cursor: "pointer",
                  display: "flex",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </div>
        </div>
      </div>
      {/* ✅ Select All Banner (above table) */}
      {!selectAllGlobal && selectedItems.size > 0 && (
        <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
          <span>
            {selectedItems.size <= items.length
              ? selectedItems.size === 1
                ? "1 user on this page is selected."
                : `${selectedItems.size} users on this page are selected.`
              : `Selected ${selectedItems.size} users.`}
          </span>
          {selectedItems.size < totalUsers && (
            <button
              className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
              onClick={async () => {
                const result = await showConfirmationDialog(
                  `You are about to select all ${totalUsers} users. This may affect global actions. Do you want to continue?`,
                  "Select All Users",
                  "Cancel"
                );
                if (result.isConfirmed) {
                  setSelectAllGlobal(true);
                }
              }}
            >
              Select all {totalUsers} users
            </button>
          )}
        </div>
      )}
      {selectAllGlobal && (
        <div className="alert alert-success d-flex justify-content-between align-items-center mb-3">
          <span>All {totalUsers} users are selected.</span>
          <button
            className="btn btn-danger"
            onClick={() => {
              setSelectAllGlobal(false);
              setSelectedItems(new Set());
            }}
          >
            Clear Selection
          </button>
        </div>
      )}
      {/* A row that is not paused under the paused filter means the server
          ignored the filter (a backend without introduction pauses). Say so
          rather than let everyone read as paused. Only for rows FETCHED with
          the filter (listPausedFilter): right after the filter changes, the
          old unfiltered rows are still on screen for a render before the
          fetch starts (review 2026-09-27). */}
      {pausedFilter === "only" &&
        listPausedFilter === "only" &&
        !loading &&
        items.some((u) => !isPaused(u)) && (
        <div className="alert alert-warning mx-24 mt-3 mb-0">
          The server did not apply the paused filter, so this list includes members who are not paused (they have no
          Paused badge). It works once the backend with introduction pauses is deployed.
        </div>
      )}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>
                  <div className="form-check style-check d-flex align-items-center">
                    <input
                      className="form-check-input radius-4 border input-form-dark"
                      type="checkbox"
                      // id="selectAll"
                      // onChange={handleSelectAll}
                      // checked={
                      //   selectedItems.length === items.length &&
                      //   items.length > 0
                      // }
                      ref={headerCheckboxRef}
                      checked={isHeaderChecked()}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{
                        backgroundColor: isHeaderIndeterminate()
                          ? "transparent"
                          : undefined,
                        borderColor: isHeaderIndeterminate()
                          ? "#d1d5db80"
                          : undefined,
                      }}
                    />
                  </div>
                </th>
                <th
                  onClick={() => handleSort("fullName")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "fullName" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "fullName" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>

                <th
                  onClick={() => handleSort("email")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Email
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "email" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "email" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("age")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Age
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "age" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "age" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Height</th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Signup date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "asc" ? "active" : ""}`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "desc" ? "active" : ""}`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((user) => (
                  <tr key={user._id}>
                    <td>
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border border-neutral-400"
                          type="checkbox"
                          value={user._id}
                          checked={isSelected(user._id) || selectAllGlobal}
                          onChange={() => handleSelectItem(user._id)}
                        />
                      </div>
                    </td>
                    <td>
                      <span
                        className="d-inline-block text-truncate"
                        style={{ maxWidth: "150px" }}
                        title={user?.fullName || "N/A"}
                      >
                        {user?.fullName || "N/A"}
                      </span>
                      {isPaused(user) && (
                        <span
                          className="badge bg-warning-100 text-warning-700 ms-1 align-top"
                          title={pauseLine(user.introPause)}
                        >
                          Paused
                        </span>
                      )}
                      {isNameFlagged(user) && (
                        <Link
                          to={`/user/view-user/${user._id}`}
                          className="badge bg-danger-100 text-danger-700 ms-1 align-top"
                          title={flaggedLine(user.nameReview)}
                        >
                          {NAME_REVIEW_LABEL}
                        </Link>
                      )}
                    </td>
                    <td>{user?.email || "N/A"}</td>
                    <td>{user?.age || "N/A"}</td>
                    <td>{user?.height || "N/A"}</td>
                    <td>{moment(user?.createdAt).format("DD-MM-YYYY")}</td>
                    <td>
                      {user?.deletedAt ? (
                        <span
                          className="px-24 py-4 radius-4 fw-medium text-sm bg-danger-focus text-danger-600 border border-danger-main"
                          title={`Account deleted ${moment(user.deletedAt).format("DD-MM-YYYY")}`}
                        >
                          Deleted
                        </span>
                      ) : !shouldCheckPermissions || canEdit ? (
                        <select
                          className={`form-select form-select-sm px-20 py-4 fw-medium text-sm radius-4
        ${user.status === "active"
                              ? "border-success-main text-success-600"
                              : user.status === "suspended"
                                ? "border-warning-main text-warning-600"
                                : user.status === "banned"
                                  ? "border-danger-main text-danger-600"
                                  : "border-neutral-400 text-neutral-600"
                            }`}
                          value={user.status}
                          onChange={(e) =>
                            updateStatus(user._id, user.status, e.target.value)
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                          }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                          <option value="suspended">Suspended</option>
                          <option value="banned">Banned</option>
                        </select>
                      ) : (
                        <span
                          className={`px-24 py-4 radius-4 fw-medium text-sm opacity-50
        ${user.status === "active"
                              ? "bg-success-focus text-success-600 border border-success-main"
                              : user.status === "suspended"
                                ? "bg-warning-focus text-warning-600 border border-warning-main"
                                : user.status === "banned"
                                  ? "bg-danger-focus text-danger-600 border border-danger-main"
                                  : "bg-neutral-200 text-neutral-600 border border-neutral-400"
                            }`}
                          title="You don’t have permission to change status"
                        >
                          {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        </span>
                      )}
                    </td>

                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {/* Account view: signup date + why they deleted.
                            Works for deleted accounts too (the full
                            profile view/edit still 404 for them). */}
                        <button
                          title={user?.deletedAt ? "Why they left + signup date" : "Account (signup date)"}
                          onClick={() => openLifecycle(user)}
                          className="bg-lilac-focus bg-hover-lilac-200 text-lilac-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                        >
                          <Icon icon="mdi:information-outline" className="icon text-xl" />
                        </button>
                        {!user?.deletedAt && (!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() =>
                              navigate(`/user/view-user/${user._id}`)
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon text-xl"
                            />

                          </button>
                        )}
                        {!user?.deletedAt && (!shouldCheckPermissions || canEdit) && (
                          <button
                            title="Edit"
                            onClick={() =>
                              navigate(`/user/edit-user/${user._id}`)
                            }
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"

                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                        )}
                        {!user?.deletedAt && (!shouldCheckPermissions || canDelete) && (
                          <button
                            title="Delete (soft, keeps the record)"
                            type="button"
                            onClick={() => handleDeleteUser(user._id)}
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canDelete) && (
                          <button
                            title="Permanently delete (cannot be undone)"
                            type="button"
                            onClick={() => handleHardDelete(user)}
                            className="bg-danger-600 bg-hover-danger-700 text-white fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="mdi:delete-forever-outline" className="menu-icon" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Users Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24 cz-footer">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="d-flex align-items-center gap-2">
              <span className="text-secondary-light">Rows per page</span>
              <select
                className="cz-pagesize"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            <span>
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalUsers)} of {totalUsers}{" "}
              entries
            </span>
          </div>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {getPaginationRange().map((page) => {
              // Return JSX for pagination items
              return (
                <li key={page} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                      }`}
                    to="#"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Link>
                </li>
              );
            })}

            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {lifecycle && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,0.72)", zIndex: 2000 }}
          onClick={closeLifecycle}
        >
          <div
            className="radius-12 shadow-lg"
            style={{
              width: "min(460px, 92vw)",
              background: "#1b2431",
              border: "1px solid #2a3543",
              overflow: "hidden",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="d-flex align-items-center justify-content-between px-24 py-16"
              style={{ background: "#131b26", borderBottom: "1px solid #2a3543" }}
            >
              <span className="fw-bold text-white">
                {lifecycle.header?.fullName || lifecycle.data?.fullName || "Account"}
              </span>
              <button
                type="button"
                className="bg-neutral-200 text-neutral-700 w-32-px h-32-px d-flex align-items-center justify-content-center rounded-circle"
                onClick={closeLifecycle}
              >
                <Icon icon="ic:round-close" />
              </button>
            </div>
            <div className="px-24 py-16" style={{ background: "#1b2431" }}>
              {lifecycle.loading ? (
                <div className="text-center py-3">
                  <span className="loader-15"></span>
                </div>
              ) : lifecycle.error || !lifecycle.data ? (
                <div className="alert alert-danger py-2 mb-0">
                  Could not load this account.
                </div>
              ) : (
                <>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-secondary-light">Signed up</span>
                    <span className="fw-medium">
                      {lifecycle.data.signupDate
                        ? moment(lifecycle.data.signupDate).format("DD MMM YYYY, h:mm a")
                        : "N/A"}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-secondary-light">Email</span>
                    <span className="fw-medium text-truncate" style={{ maxWidth: 240 }}>
                      {lifecycle.data.email || "N/A"}
                    </span>
                  </div>
                  {/* Referral trail (2026-09-06): invite code, who invited
                      them, whom they invited, and the month-of-Club payout. */}
                  {lifecycle.data.referral && (
                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-secondary-light">Referrals</span>
                      <span className="fw-medium text-end" style={{ maxWidth: 260 }}>
                        <div className="text-sm">
                          Code <code>{lifecycle.data.referral.code || "none"}</code>
                        </div>
                        {lifecycle.data.referral.referredBy && (
                          <div className="text-xs text-secondary-light">
                            Invited by {lifecycle.data.referral.referredBy.name}
                            {lifecycle.data.referral.rewardAt ? " · Club month paid" : " · reward pending first Vault"}
                          </div>
                        )}
                        <div className="text-xs text-secondary-light">
                          {lifecycle.data.referral.invited.length === 0
                            ? "Invited nobody yet"
                            : "Invited " +
                              lifecycle.data.referral.invited
                                .map((i) => i.name + (i.rewarded ? " ✓" : ""))
                                .join(", ")}
                        </div>
                      </span>
                    </div>
                  )}
                  {/* Show-up tier (2026-09-04): what this member has actually
                      done with their introductions. */}
                  {lifecycle.data.showUp && (
                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-secondary-light">Shows up</span>
                      <span className="fw-medium text-end">
                        <span
                          className={
                            "badge " +
                            (lifecycle.data.showUp.tier === "proven"
                              ? "bg-success-100 text-success-700"
                              : lifecycle.data.showUp.tier === "ghost"
                                ? "bg-danger-100 text-danger-600"
                                : "bg-neutral-200 text-neutral-600")
                          }
                        >
                          {lifecycle.data.showUp.tier === "proven"
                            ? "Proven"
                            : lifecycle.data.showUp.tier === "ghost"
                              ? "Ghost"
                              : "Untested"}
                        </span>
                        <div className="text-xs text-secondary-light">
                          {lifecycle.data.showUp.vaultsFinished} Vault{lifecycle.data.showUp.vaultsFinished === 1 ? "" : "s"} finished ·{" "}
                          {lifecycle.data.showUp.revealsChosen} reveal{lifecycle.data.showUp.revealsChosen === 1 ? "" : "s"} chosen ·{" "}
                          {lifecycle.data.showUp.served} served
                        </div>
                      </span>
                    </div>
                  )}
                  {lifecycle.data.push && (
                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-secondary-light">Push notifications</span>
                      <span className={`fw-medium text-end ${lifecycle.data.push.hasToken ? "" : "text-warning-600"}`} style={{ maxWidth: 260 }}>
                        {lifecycle.data.push.hasToken
                          ? `Reachable${lifecycle.data.push.platform ? ", " + lifecycle.data.push.platform : ""}`
                          : "No phone on file, email only"}
                        {lifecycle.data.push.lastDroppedAt && (
                          <span className="d-block text-xs text-secondary-light">
                            token last died {moment(lifecycle.data.push.lastDroppedAt).format("DD MMM, h:mm a")}
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                  {/* Introductions paused (founder go 2026-09-27): whether they
                      asked to stop being introduced, since when, by whom and
                      why. Eastern, like the member page line. */}
                  {lifecycleIntroLine(lifecycle.data.introPause, !!lifecycle.data.deletedAt) && (
                    <div className="d-flex justify-content-between py-2 border-bottom" data-testid="lifecycle-intro-line">
                      <span className="text-secondary-light">Introductions</span>
                      <span
                        className={`fw-medium text-end ${isPaused(lifecycle.data) ? "text-warning-600" : ""}`}
                        style={{ maxWidth: 260 }}
                      >
                        {lifecycleIntroLine(lifecycle.data.introPause, !!lifecycle.data.deletedAt)}
                      </span>
                    </div>
                  )}
                  {lifecycle.data.deletedAt ? (
                    <>
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-secondary-light">Deleted</span>
                        <span className="fw-medium text-danger-600">
                          {moment(lifecycle.data.deletedAt).format("DD MMM YYYY, h:mm a")}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-secondary-light">Reason</span>
                        <span className="fw-medium text-end" style={{ maxWidth: 260 }}>
                          {lifecycle.data.deletion?.reasonLabel
                            ? lifecycle.data.deletion.reasonLabel
                            : lifecycle.data.deletion?.logged
                              ? "No reason given"
                              : "Not recorded (older or admin deletion)"}
                        </span>
                      </div>
                      {lifecycle.data.deletion?.note && (
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-secondary-light">In their words</span>
                          <span className="fw-medium text-end fst-italic" style={{ maxWidth: 260 }}>
                            {lifecycle.data.deletion.note}
                          </span>
                        </div>
                      )}
                      {lifecycle.data.deletion?.pocketState && (
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-secondary-light">Stage when they left</span>
                          <span className="fw-medium">
                            {lifecycle.data.deletion.pocketState}
                            {lifecycle.data.deletion.profileStepsCount != null
                              ? ` · ${lifecycle.data.deletion.profileStepsCount} profile steps`
                              : ""}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="d-flex justify-content-between py-2 border-bottom">
                      <span className="text-secondary-light">Status</span>
                      <span className="fw-medium text-success-600">
                        {(lifecycle.data.status || "active")
                          .charAt(0)
                          .toUpperCase() + (lifecycle.data.status || "active").slice(1)}
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserListLayer;
